import type { ReactNode } from 'react';

import { formatCalendarDate } from '@/lib/format';
import { PALETTES } from '@/lib/palette';

import { TIME_ZONE_OPTIONS } from './settings';
import { useSetting } from './useSettings';

/**
 * Units, date/time and the default palette — `PLAN.md` §6 Phase 4. Backup is
 * its own feature (`features/backup`) composed alongside this one on the
 * `/settings` route, not imported here — `ARCHITECTURE.md` §2's rule against
 * one feature reaching into another applies to Settings same as any other.
 *
 * Every control writes straight to `SettingsRepository` through `useSetting`
 * and takes effect immediately — there is no "Save" button, the same as the
 * export composer's own panel of selects.
 */

const SELECT_CLASS =
  'border-border bg-surface rounded-control border px-2.5 py-1.5 text-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="border-border bg-surface rounded-container border p-4">
      <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
      <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{description}</p>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export function SettingsPage() {
  const [altitudeUnit, setAltitudeUnit] = useSetting('altitudeUnit');
  const [speedUnit, setSpeedUnit] = useSetting('speedUnit');
  const [distanceUnit, setDistanceUnit] = useSetting('distanceUnit');
  const [dateFormat, setDateFormat] = useSetting('dateFormat');
  const [timeZone, setTimeZone] = useSetting('timeZone');
  const [defaultPaletteId, setDefaultPaletteId] = useSetting('defaultPaletteId');

  return (
    <>
      <h1 className="text-lg font-semibold tracking-tight">Settings</h1>

      <Section title="Units" description="Used for map legends and image exports.">
        <Field label="Altitude">
          <select
            className={SELECT_CLASS}
            value={altitudeUnit}
            onChange={(e) => {
              setAltitudeUnit(e.target.value === 'm' ? 'm' : 'ft');
            }}
          >
            <option value="ft">Feet (ft)</option>
            <option value="m">Metres (m)</option>
          </select>
        </Field>
        <Field label="Speed">
          <select
            className={SELECT_CLASS}
            value={speedUnit}
            onChange={(e) => {
              setSpeedUnit(e.target.value === 'kmh' ? 'kmh' : 'kt');
            }}
          >
            <option value="kt">Knots (kt)</option>
            <option value="kmh">Kilometres per hour (km/h)</option>
          </select>
        </Field>
        <Field label="Distance">
          <select
            className={SELECT_CLASS}
            value={distanceUnit}
            onChange={(e) => {
              setDistanceUnit(e.target.value === 'km' ? 'km' : 'nm');
            }}
          >
            <option value="nm">Nautical miles (nm)</option>
            <option value="km">Kilometres (km)</option>
          </select>
        </Field>
      </Section>

      <Section
        title="Date and time"
        description="Flight times are stored as UTC. Select a time zone for display."
      >
        <Field label="Date format">
          <select
            className={SELECT_CLASS}
            value={dateFormat}
            onChange={(e) => {
              setDateFormat(e.target.value === 'iso' ? 'iso' : 'nz');
            }}
          >
            {/*
              Rendered by the formatter itself: a hand-typed "16 Sep 2026"
              disagreed with the "16 Sept 2026" the app then printed, because
              the platform's en-NZ month abbreviation is "Sept".
            */}
            <option value="nz">{formatCalendarDate('2026-09-16', { format: 'nz' })}</option>
            <option value="iso">{formatCalendarDate('2026-09-16', { format: 'iso' })}</option>
          </select>
        </Field>
        <Field label="Timezone">
          <select
            className={SELECT_CLASS}
            value={timeZone}
            onChange={(e) => {
              setTimeZone(e.target.value);
            }}
          >
            {TIME_ZONE_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
      </Section>

      <Section title="Map" description="Default colour palette for maps and image exports.">
        <Field label="Default palette">
          <select
            className={SELECT_CLASS}
            value={defaultPaletteId}
            onChange={(e) => {
              setDefaultPaletteId(e.target.value);
            }}
          >
            {PALETTES.map((palette) => (
              <option key={palette.id} value={palette.id}>
                {palette.label}
              </option>
            ))}
          </select>
        </Field>
      </Section>
    </>
  );
}
